import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Image, Send, X, Smile, BarChart3, Film, Loader2, Camera, AlertCircle, User, AtSign, Share2, Mic, MicOff, Play, Pause, Music, Shield, Clock, CalendarClock, Link2, MoreHorizontal, Sparkles, PenTool, CameraIcon, Tag, Check } from 'lucide-react';
import { nip19, getEventHash } from 'nostr-tools';

import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useNostrQuery, getCachedProfile, fetchProfile, findProfileByName } from '../hooks/useNostrQuery';
import { useFollows } from '../hooks/useFollows';
import { useAccountMaturity } from '../hooks/useTrustScore';
import { uploadMedia, isVideoFile, isAudioFile, uploadAudio, UploadSizeError, type CompressionProgress } from '../lib/imageUpload';
import { BCHLogo } from './Layout';
import { getStoredReferrer, clearStoredReferrer, hasConfirmedReferral, markReferralConfirmed, REFERRAL_KIND, REFERRAL_DRTAG } from '../lib/referral';
import CrossPostModal from './CrossPostModal';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';
import { schedulePost, formatScheduledTime, getSchedulePresets } from '../lib/scheduledPosts';

interface ComposerProps {
  replyTo?: { id: string; pubkey: string; rootId?: string };
  onPublished?: () => void;
  placeholder?: string;
}

// ── EXIF camera detection ────────────────────────────────────────
// Reads enough of a JPEG/HEIC file to detect if it has EXIF camera
// metadata (Make/Model tags). This tells us the image was taken with
// a real camera rather than downloaded or AI-generated.
// Uses a minimal EXIF parser — no external library needed.

async function hasExifCameraData(file: File): Promise<boolean> {
  // Only JPEG and HEIC files contain EXIF
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  const isJpeg = type === 'image/jpeg' || type === 'image/jpg' || name.endsWith('.jpg') || name.endsWith('.jpeg');
  const isHeic = type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif');
  if (!isJpeg && !isHeic) return false;

  try {
    // Read the first 128KB — EXIF is always near the start
    const slice = file.slice(0, 131072);
    const buffer = await slice.arrayBuffer();
    const view = new DataView(buffer);

    // For JPEG: find the EXIF APP1 marker (0xFFE1)
    if (isJpeg) {
      if (view.getUint16(0) !== 0xFFD8) return false; // Not a JPEG
      let offset = 2;
      while (offset < view.byteLength - 4) {
        const marker = view.getUint16(offset);
        if (marker === 0xFFE1) {
          // APP1 — check for "Exif\0\0"
          const exifHeader = String.fromCharCode(
            view.getUint8(offset + 4), view.getUint8(offset + 5),
            view.getUint8(offset + 6), view.getUint8(offset + 7),
          );
          if (exifHeader === 'Exif') {
            return scanTiffForCamera(view, offset + 10);
          }
        }
        // Skip to next marker
        const segLen = view.getUint16(offset + 2);
        offset += 2 + segLen;
      }
    }

    // For HEIC: simplified — just search for common camera maker strings in first 128KB
    if (isHeic) {
      const text = new TextDecoder('ascii', { fatal: false }).decode(buffer);
      const cameraKeywords = ['Apple', 'Samsung', 'Google', 'Canon', 'Nikon', 'Sony',
        'Huawei', 'Xiaomi', 'OPPO', 'OnePlus', 'Motorola', 'LG', 'Pixel',
        'iPhone', 'iPad', 'Galaxy', 'DCIM'];
      return cameraKeywords.some(kw => text.includes(kw));
    }
  } catch {
    // If EXIF parsing fails, don't assume it's a camera photo
  }
  return false;
}

/** Scan a TIFF IFD for camera Make (0x010F) or Model (0x0110) tags */
function scanTiffForCamera(view: DataView, tiffStart: number): boolean {
  try {
    // Byte order: 'II' (little-endian) or 'MM' (big-endian)
    const bo = view.getUint16(tiffStart);
    const le = bo === 0x4949;
    const getU16 = (o: number) => view.getUint16(o, le);
    const getU32 = (o: number) => view.getUint32(o, le);

    // Verify TIFF magic (42)
    if (getU16(tiffStart + 2) !== 0x002A) return false;

    // Read IFD0 offset
    let ifdOffset = tiffStart + getU32(tiffStart + 4);
    const numEntries = getU16(ifdOffset);
    ifdOffset += 2;

    for (let i = 0; i < numEntries && i < 100; i++) {
      const entryOffset = ifdOffset + i * 12;
      if (entryOffset + 12 > view.byteLength) break;
      const tag = getU16(entryOffset);
      // 0x010F = Make, 0x0110 = Model
      if (tag === 0x010F || tag === 0x0110) {
        return true; // Has camera maker/model — it's a camera photo
      }
    }
  } catch {
    // Parse error — not conclusive
  }
  return false;
}

/** Detect if a file is likely from device camera based on filename patterns */
function looksLikeCameraFile(file: File): boolean {
  const name = file.name.toLowerCase();
  // Common camera photo filename patterns
  return /^(img|photo|image|dsc|dcim|pic)[-_]?\d/i.test(name) ||
    /^(20\d{2})(0\d|1[0-2])(0\d|[12]\d|3[01])[-_]?\d/i.test(name) || // YYYYMMDD_HHMMSS
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\./i.test(name) || // UUID (iOS)
    name.startsWith('pxl_') || // Pixel phones
    name.startsWith('mvimg_') || // Motion photos
    /^screenshot/i.test(name); // Screenshots are also "original"
}

/** Source type for each attachment — helps with auto-labeling */
type AttachmentSource = 'camera' | 'file-picker' | 'gif-picker' | 'clipboard' | 'recording' | 'unknown';

/** Detect the origin of a media file */
async function detectMediaSource(file: File): Promise<AttachmentSource> {
  // Check EXIF first — most reliable signal
  const hasCamera = await hasExifCameraData(file);
  if (hasCamera) return 'camera';

  // Fall back to filename heuristics
  if (looksLikeCameraFile(file)) return 'camera';

  return 'file-picker';
}

// ── Draft auto-save ─────────────────────────────────────────────
// Saves composer state (text, attachments, poll) to localStorage so it
// survives accidental navigation, page refresh, or misclicks.
// Uses a different key for replies vs top-level posts.

type ContentLabel = 'ai-generated' | 'manipulated' | 'original';

interface DraftData {
  text: string;
  attachments?: { url: string; type: 'image' | 'video' | 'audio'; name?: string }[];
  pollOptions?: string[];
  showPoll?: boolean;
  contentLabels?: ContentLabel[];
  savedAt: number; // timestamp for staleness checks
}

const DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours — discard ancient drafts

function getDraftKey(replyToId?: string): string {
  return replyToId ? `bchnostr:draft:reply:${replyToId}` : 'bchnostr:draft:compose';
}

function loadDraft(replyToId?: string): DraftData | null {
  try {
    const raw = localStorage.getItem(getDraftKey(replyToId));
    if (!raw) return null;
    // Support both old format (plain string) and new format (JSON object)
    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw) as DraftData;
      // Discard drafts older than 24h
      if (parsed.savedAt && Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS) {
        localStorage.removeItem(getDraftKey(replyToId));
        return null;
      }
      return parsed;
    }
    // Old format: plain text string — migrate
    return raw.trim() ? { text: raw, savedAt: Date.now() } : null;
  } catch { return null; }
}

function saveDraft(data: DraftData, replyToId?: string) {
  try {
    const key = getDraftKey(replyToId);
    const hasContent = (data.text || '').trim() ||
      (data.attachments && data.attachments.length > 0) ||
      (data.showPoll && data.pollOptions?.some(o => o.trim()));
    if (hasContent) {
      localStorage.setItem(key, JSON.stringify({ ...data, savedAt: Date.now() }));
    } else {
      localStorage.removeItem(key);
    }
  } catch {}
}

function clearDraft(replyToId?: string) {
  try { localStorage.removeItem(getDraftKey(replyToId)); } catch {}
}

export default function Composer({ replyTo, onPublished, placeholder }: ComposerProps) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const maturity = useAccountMaturity(user?.pubkey);
  const [initialDraft] = useState(() => loadDraft(replyTo?.id));
  const [content, setContent] = useState(() => initialDraft?.text || '');
  const [draftRestored, setDraftRestored] = useState(() => !!initialDraft?.text?.trim() || !!(initialDraft?.attachments?.length));
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<{ url: string; type: 'image' | 'video' | 'audio'; name?: string }[]>(
    () => initialDraft?.attachments || []
  );
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showPollCreator, setShowPollCreator] = useState(() => !!initialDraft?.showPoll);
  const [contentLabels, setContentLabels] = useState<ContentLabel[]>(() => initialDraft?.contentLabels || []);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  // Track where attachments came from for auto-labeling
  const [attachmentSources, setAttachmentSources] = useState<AttachmentSource[]>([]);
  // Auto-suggestion banner state: shown once when a camera photo is detected
  const [labelSuggestion, setLabelSuggestion] = useState<{ type: 'original-detected' | 'paste-hint'; dismissed: boolean } | null>(null);
  const [pollOptions, setPollOptions] = useState(() => initialDraft?.pollOptions?.length ? initialDraft.pollOptions : ['', '']);
  const [pollDuration, setPollDuration] = useState(24);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionDropdown, setMentionDropdown] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<{ message: string; upgradeUrl?: string } | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [showCrossPost, setShowCrossPost] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleConfirmed, setScheduleConfirmed] = useState(false);
  // ── Hyperlink insertion ────────────────────────────────────────────
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showMoreTools, setShowMoreTools] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const linkUrlRef = useRef<HTMLInputElement>(null);
  // ── Audio recording ───────────────────────────────────────────────
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioFileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      mediaRecorderRef.current?.stop();
    };
  }, []);

  // ── Auto-label suggestion after attachment source detection ──────
  // When a camera photo is detected, auto-add "original" label and show
  // a confirmation banner. When a clipboard paste is detected, show a hint.
  const handleSourceDetected = useCallback((source: AttachmentSource) => {
    setAttachmentSources(prev => [...prev, source]);
    if (source === 'camera') {
      // Auto-add "original" label if not already present
      setContentLabels(prev => prev.includes('original') ? prev : [...prev, 'original']);
      setLabelSuggestion({ type: 'original-detected', dismissed: false });
    } else if (source === 'clipboard') {
      // Subtle hint that pasted content may not be original
      if (!labelSuggestion || labelSuggestion.dismissed) {
        setLabelSuggestion({ type: 'paste-hint', dismissed: false });
      }
    } else if (source === 'gif-picker') {
      // GIF from picker — remove "original" if it was the only reason
      // (don't auto-tag GIFs as original content)
    }
  }, [labelSuggestion]);

  // ── Auto-save draft to localStorage ──────────────────────────────
  // Debounced save (500ms) on every change + immediate save on unmount.
  // Also saves attachments and poll options so the full state survives.
  const contentRef = useRef(content);
  const attachmentsRef = useRef(attachments);
  const pollOptionsRef = useRef(pollOptions);
  const showPollRef = useRef(showPollCreator);
  const contentLabelsRef = useRef(contentLabels);
  contentRef.current = content;
  attachmentsRef.current = attachments;
  pollOptionsRef.current = pollOptions;
  showPollRef.current = showPollCreator;
  contentLabelsRef.current = contentLabels;

  const saveDraftNow = useCallback(() => {
    saveDraft({
      text: contentRef.current,
      attachments: attachmentsRef.current.length > 0 ? attachmentsRef.current : undefined,
      pollOptions: showPollRef.current ? pollOptionsRef.current : undefined,
      showPoll: showPollRef.current || undefined,
      contentLabels: contentLabelsRef.current.length > 0 ? contentLabelsRef.current : undefined,
      savedAt: Date.now(),
    }, replyTo?.id);
  }, [replyTo?.id]);

  useEffect(() => {
    const timer = setTimeout(saveDraftNow, 500);
    return () => clearTimeout(timer);
  }, [content, attachments, pollOptions, showPollCreator, contentLabels, saveDraftNow]);

  // Immediate save on unmount — prevents losing the last edit if the user
  // navigates away within the 500ms debounce window.
  useEffect(() => {
    return () => { saveDraftNow(); };
  }, [saveDraftNow]);

  // ── Warn before leaving with unsaved content ──────────────────
  // Shows a browser confirmation dialog if the user tries to close/refresh
  // the tab or navigate away (via browser back/forward) with draft content.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      const hasContent = contentRef.current.trim() ||
        attachmentsRef.current.length > 0 ||
        (showPollRef.current && pollOptionsRef.current.some(o => o.trim()));
      if (hasContent) {
        e.preventDefault();
        // Save immediately before the page unloads
        saveDraftNow();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveDraftNow]);

  // Track mentioned pubkeys for p-tags
  const [mentionedPubkeys, setMentionedPubkeys] = useState<{ pubkey: string; name: string }[]>([]);

  // ── Mention system ─────────────────────────────────────────────
  // Sources: follows, recent community posters, localStorage profile cache,
  // and live relay search for queries with 2+ characters.

  const { follows } = useFollows(user?.pubkey);
  const [mentionSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 14);
  const { events: communityPosts } = useNostrQuery({
    filters: [
      // Active BCH Nostr members — #bchnostr is a relay-indexable single-letter
      // 't' tag that every in-app post now carries (unlike the 'client' tag,
      // which most relays don't index for filtering).
      { kinds: [1], '#t': ['bchnostr'], limit: 200, since: mentionSince },
      { kinds: [1], '#t': ['bch'], limit: 200, since: mentionSince },
      { kinds: [1], '#t': ['bitcoincash'], limit: 200, since: mentionSince },
    ],
  });

  // Collect pubkeys: follows + community posters (expanded to 200)
  const allMentionPubkeys = useMemo(() => {
    const pks = new Set<string>(follows);
    communityPosts.forEach(e => pks.add(e.pubkey));
    return [...pks].slice(0, 200);
  }, [follows.length, communityPosts.length]);

  // Fetch profiles for known pubkeys
  const [stablePubkeys, setStablePubkeys] = useState<string[]>([]);
  useEffect(() => {
    if (allMentionPubkeys.length > stablePubkeys.length) {
      setStablePubkeys(allMentionPubkeys);
    }
  }, [allMentionPubkeys.length]);

  const { events: mentionProfiles } = useNostrQuery({
    filters: stablePubkeys.length > 0 ? [{ kinds: [0], authors: stablePubkeys, limit: 200 }] : [],
    enabled: stablePubkeys.length > 0,
  });

  // Live relay search — triggered when local results are sparse and query is 2+ chars
  const [liveSearchQuery, setLiveSearchQuery] = useState('');
  const [liveSearchResults, setLiveSearchResults] = useState<{ pubkey: string; name: string; picture?: string; nip05?: string }[]>([]);
  const [liveSearching, setLiveSearching] = useState(false);
  const liveSearchTimer = useRef<any>(null);

  // Debounced live search when local results are few
  useEffect(() => {
    if (liveSearchTimer.current) clearTimeout(liveSearchTimer.current);
    if (!mentionDropdown || liveSearchQuery.length < 2) {
      setLiveSearchResults([]);
      setLiveSearching(false);
      return;
    }
    setLiveSearching(true);
    liveSearchTimer.current = setTimeout(async () => {
      try {
        // Search via NIP-50 text search on profile relay
        const query = liveSearchQuery.toLowerCase();
        const results: typeof liveSearchResults = [];
        const seen = new Set<string>();

        // Try NIP-50 search on relays that support it
        const searchRelays = ['wss://relay.nostr.net', 'wss://search.nos.today'];
        const promises = searchRelays.map(relayUrl =>
          new Promise<void>((resolve) => {
            try {
              const ws = new WebSocket(relayUrl);
              const subId = 'ms_' + Math.random().toString(36).slice(2, 8);
              const timeout = setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 4000);
              ws.onopen = () => {
                ws.send(JSON.stringify(['REQ', subId, { kinds: [0], search: query, limit: 10 }]));
              };
              ws.onmessage = (msg) => {
                try {
                  const data = JSON.parse(msg.data);
                  if (data[0] === 'EVENT' && data[2]) {
                    const ev = data[2];
                    if (seen.has(ev.pubkey)) return;
                    seen.add(ev.pubkey);
                    const p = JSON.parse(ev.content);
                    const name = p.display_name || p.name || '';
                    if (name) {
                      results.push({ pubkey: ev.pubkey, name, picture: p.picture, nip05: p.nip05 });
                    }
                  }
                  if (data[0] === 'EOSE') { clearTimeout(timeout); try { ws.close(); } catch {} resolve(); }
                } catch {}
              };
              ws.onerror = () => { clearTimeout(timeout); resolve(); };
            } catch { resolve(); }
          })
        );
        await Promise.allSettled(promises);
        setLiveSearchResults(results);
      } catch {
        setLiveSearchResults([]);
      } finally {
        setLiveSearching(false);
      }
    }, 300);

    return () => { if (liveSearchTimer.current) clearTimeout(liveSearchTimer.current); };
  }, [liveSearchQuery, mentionDropdown]);

  // Build mention suggestions from ALL sources
  const mentionSuggestions = useMemo(() => {
    if (!mentionDropdown) return [];
    const query = mentionQuery.toLowerCase();
    const seen = new Set<string>();
    const results: { pubkey: string; name: string; picture?: string; nip05?: string; isFollow: boolean }[] = [];

    // Helper to add a profile match
    const addResult = (pubkey: string, profile: any, source: 'relay' | 'cache' | 'live') => {
      if (seen.has(pubkey)) return;
      const name = profile.display_name || profile.name || '';
      const nip05 = profile.nip05 || '';
      if (query.length === 0 || name.toLowerCase().includes(query) || nip05.toLowerCase().includes(query) || pubkey.startsWith(query)) {
        seen.add(pubkey);
        results.push({
          pubkey,
          name: name || pubkey.slice(0, 8),
          picture: profile.picture,
          nip05,
          isFollow: follows.includes(pubkey),
        });
      }
    };

    // Source 1: Profiles fetched from relay query (follows + community)
    for (const ev of mentionProfiles) {
      try {
        const p = JSON.parse(ev.content);
        addResult(ev.pubkey, p, 'relay');
      } catch {}
    }

    // Source 2: localStorage profile cache (up to 500 profiles from browsing)
    // This catches anyone the user has seen in their feed, on profiles, etc.
    if (query.length > 0) {
      try {
        const raw = localStorage.getItem('bchnostr:profiles');
        if (raw) {
          const cached = JSON.parse(raw) as Record<string, any>;
          for (const [pk, profile] of Object.entries(cached)) {
            if (profile && !seen.has(pk)) {
              addResult(pk, profile, 'cache');
            }
          }
        }
      } catch {}
    }

    // Source 3: Live relay search results (NIP-50)
    for (const lr of liveSearchResults) {
      if (!seen.has(lr.pubkey)) {
        seen.add(lr.pubkey);
        results.push({
          pubkey: lr.pubkey,
          name: lr.name,
          picture: lr.picture,
          nip05: lr.nip05,
          isFollow: follows.includes(lr.pubkey),
        });
      }
    }

    // Sort: follows first, then alphabetical
    results.sort((a, b) => {
      if (a.isFollow && !b.isFollow) return -1;
      if (!a.isFollow && b.isFollow) return 1;
      return a.name.localeCompare(b.name);
    });

    return results.slice(0, 15);
  }, [mentionDropdown, mentionQuery, mentionProfiles, follows, liveSearchResults]);

  // ── Auto-resize textarea to fit content ────────────────────────
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto'; // Reset to recalculate
    el.style.height = Math.min(el.scrollHeight, 400) + 'px';
  }, []);

  // Re-fit when content changes programmatically (draft restore, mention insert, etc.)
  useEffect(() => { requestAnimationFrame(autoResize); }, [content, autoResize]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setContent(val);
    if (draftRestored) setDraftRestored(false); // dismiss indicator once user starts editing
    const pos = e.target.selectionStart || 0;
    setCursorPos(pos);

    // Auto-resize after content changes
    requestAnimationFrame(autoResize);

    // Check for @mention trigger — match @ at word boundary or start of line
    const before = val.slice(0, pos);
    const atMatch = before.match(/(?:^|[\s(])@(\w*)$/);
    if (atMatch) {
      const q = atMatch[1];
      setMentionQuery(q);
      setMentionDropdown(true);
      setMentionIndex(0);
      // Trigger live relay search for queries with 2+ chars
      if (q.length >= 2) {
        setLiveSearchQuery(q);
      } else {
        setLiveSearchQuery('');
      }
    } else {
      setMentionDropdown(false);
      setMentionQuery('');
      setLiveSearchQuery('');
    }
  };

  const insertMention = (pubkey: string, name: string) => {
    const before = content.slice(0, cursorPos);
    const after = content.slice(cursorPos);
    const atIndex = before.lastIndexOf('@');

    // Use proper nostr: npub mention format
    const npub = nip19.npubEncode(pubkey);
    const newContent = before.slice(0, atIndex) + `nostr:${npub} ` + after;
    setContent(newContent);
    setMentionDropdown(false);
    setMentionQuery('');

    // Track for p-tags
    if (!mentionedPubkeys.find(m => m.pubkey === pubkey)) {
      setMentionedPubkeys(prev => [...prev, { pubkey, name }]);
    }

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newPos = atIndex + `nostr:${npub} `.length;
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 10);
  };

  // ── Clipboard paste for images ────────────────────────────────────
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    // Look for image files in the clipboard
    const imageItem = Array.from(items).find(
      item => item.kind === 'file' && item.type.startsWith('image/')
    );

    if (!imageItem) return; // No image — let the default paste (text) happen

    // Prevent the default paste behavior (don't insert binary garbage)
    e.preventDefault();

    const file = imageItem.getAsFile();
    if (!file) return;

    // Use the same upload flow as the file picker
    setUploadError(null);
    setCompressing(false);
    setCompressionProgress(0);
    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadMedia(file, setUploadProgress, (cp) => {
        if (cp.stage === 'loading' || cp.stage === 'compressing') {
          setCompressing(true);
          setCompressionProgress(cp.percent);
        } else {
          setCompressing(false);
          setCompressionProgress(100);
        }
      });
      setAttachments(prev => [...prev, result]);
      // Clipboard paste — flag as non-original source
      handleSourceDetected('clipboard');
    } catch (err) {
      console.error('Clipboard image upload failed:', err);
      setUploadError(err instanceof UploadSizeError
        ? { message: err.message, upgradeUrl: err.upgradeUrl }
        : { message: err instanceof Error ? err.message : 'Clipboard image upload failed' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCompressing(false);
      setCompressionProgress(0);
    }
  }, []);

  const insertEmoji = useCallback((emoji: string) => {
    const pos = textareaRef.current?.selectionStart ?? cursorPos;
    const newContent = content.slice(0, pos) + emoji + content.slice(pos);
    setContent(newContent);
    setShowEmojiPicker(false);
    // Restore cursor right after the inserted emoji
    setTimeout(() => {
      const newPos = pos + emoji.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 10);
  }, [content, cursorPos]);

  // ── Hyperlink insertion ─────────────────────────────────────────────
  const openLinkInput = useCallback(() => {
    setShowLinkInput(true);
    setLinkUrl('');
    setLinkText('');
    setTimeout(() => linkUrlRef.current?.focus(), 50);
  }, []);

  const insertLink = useCallback(() => {
    const url = linkUrl.trim();
    if (!url) {
      setShowLinkInput(false);
      return;
    }
    const fullUrl = url.match(/^https?:\/\//i) ? url : `https://${url}`;
    const display = linkText.trim();
    // For Nostr kind 1 posts, plain URLs get auto-linked by clients.
    // If the user provides display text, insert "display text: url" format.
    // Otherwise just insert the URL directly.
    const insertion = display ? `${display}: ${fullUrl}` : fullUrl;

    const pos = textareaRef.current?.selectionStart ?? content.length;
    const before = content.slice(0, pos);
    const after = content.slice(pos);
    // Add spacing if needed
    const needsSpaceBefore = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n');
    const needsSpaceAfter = after.length > 0 && !after.startsWith(' ') && !after.startsWith('\n');
    const newContent = before + (needsSpaceBefore ? ' ' : '') + insertion + (needsSpaceAfter ? ' ' : '') + after;
    setContent(newContent);
    setShowLinkInput(false);
    setLinkUrl('');
    setLinkText('');

    // Restore cursor after insertion
    setTimeout(() => {
      const newPos = pos + (needsSpaceBefore ? 1 : 0) + insertion.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(newPos, newPos);
    }, 10);
  }, [linkUrl, linkText, content]);

  // ── Mic recording ─────────────────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg';
      const mr = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mimeType });
        setUploadError(null);
        setUploading(true);
        setUploadProgress(0);
        try {
          const url = await uploadAudio(file, setUploadProgress);
          setAttachments(prev => [...prev, { url, type: 'audio', name: file.name }]);
        } catch (err) {
          setUploadError(err instanceof UploadSizeError
            ? { message: err.message, upgradeUrl: err.upgradeUrl }
            : { message: err instanceof Error ? err.message : 'Audio upload failed' });
        } finally {
          setUploading(false);
          setUploadProgress(0);
        }
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch (err) {
      setUploadError({ message: 'Microphone access denied. Please allow microphone permissions.' });
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    setRecording(false);
    setRecordSeconds(0);
  };

  const fmtRecordTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Rate-limit enforcement ──────────────────────────────────────
  // Local publish counter tracks posts made THIS session so the limit
  // applies instantly, without waiting for relay propagation.
  const [localPostsBump, setLocalPostsBump] = useState(0);
  const effectiveRemaining = Math.max(0, maturity.postsRemainingToday - localPostsBump);
  const isRateLimited = !maturity.isOperator && !maturity.isEstablished && effectiveRemaining <= 0;
  const showRateLimitInfo = !maturity.isOperator && !maturity.isEstablished;

  // ── Schedule helpers ──────────────────────────────────────────────
  const scheduledTimestamp = useMemo(() => {
    if (!scheduleDate || !scheduleTime) return null;
    const dt = new Date(`${scheduleDate}T${scheduleTime}`);
    if (isNaN(dt.getTime())) return null;
    const ts = Math.floor(dt.getTime() / 1000);
    // Must be in the future
    if (ts <= Math.floor(Date.now() / 1000) + 60) return null;
    return ts;
  }, [scheduleDate, scheduleTime]);

  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);

  const handleSchedule = async () => {
    if (!user || (!content.trim() && attachments.length === 0) || !scheduledTimestamp) return;
    if (scheduling) return;

    const signer = (window as any).nostr;
    if (!signer) {
      setScheduleError('No Nostr signer found. Please log in with a Nostr extension.');
      return;
    }

    setScheduling(true);
    setScheduleError(null);

    try {
      const tags: string[][] = [];
      // Add client tag for BCHNostr identification
      tags.push(['client', 'BCHNostr', '31990:df5bcaba9e74cc1764c9773ae160299dde5acf79416e2c96758dba225e6707cc:bchstr24-1', 'wss://relay.bchnostr.com']);
      // Community tag on original posts (not replies). BCH Nostr is about more
      // than Bitcoin Cash, so we no longer force #bch/#bitcoincash topic tags on
      // every post — the client:BCHNostr tag already surfaces it in the feed.
      // We add #bchnostr as a lightweight community marker. If the author wants
      // #bch/#bitcoincash they can type them and they'll be picked up below.
      if (!replyTo) {
        tags.push(['t', 'bchnostr']);
      }
      if (replyTo) {
        const rootId = replyTo.rootId || replyTo.id;
        if (rootId !== replyTo.id) {
          tags.push(['e', rootId, '', 'root']);
        }
        tags.push(['e', replyTo.id, '', 'reply']);
        tags.push(['p', replyTo.pubkey]);
      }
      mentionedPubkeys.forEach(m => {
        if (!tags.some(t => t[0] === 'p' && t[1] === m.pubkey)) {
          tags.push(['p', m.pubkey]);
        }
      });
      const npubMatches = content.matchAll(/nostr:(npub1[a-z0-9]+)/g);
      for (const match of npubMatches) {
        try {
          const decoded = nip19.decode(match[1]);
          if (decoded.type === 'npub') {
            const pk = decoded.data as string;
            if (!tags.some(t => t[0] === 'p' && t[1] === pk)) tags.push(['p', pk]);
          }
        } catch {}
      }
      const hashtagMatches = content.matchAll(/#(\w{2,})/g);
      for (const match of hashtagMatches) {
        const tag = match[1].toLowerCase();
        // Respect any hashtag the author types (including #bch/#bitcoincash),
        // de-duping against tags already added (e.g. #bchnostr).
        if (!tags.some(t => t[0] === 't' && t[1] === tag)) tags.push(['t', tag]);
      }
      attachments.forEach(att => {
        if (att.type === 'video') tags.push(['imeta', `url ${att.url}`, 'm video/mp4']);
        else if (att.type === 'audio') {
          const mime = att.url.endsWith('.mp3') ? 'audio/mpeg' : att.url.endsWith('.ogg') ? 'audio/ogg' : 'audio/webm';
          tags.push(['imeta', `url ${att.url}`, `m ${mime}`]);
        } else tags.push(['imeta', `url ${att.url}`]);
      });
      // NIP-32 content labels for scheduled posts
      if (contentLabels.length > 0) {
        tags.push(['L', 'content-origin']);
        for (const label of contentLabels) {
          tags.push(['l', label, 'content-origin']);
        }
      }
      const mediaUrls = attachments.map(att => att.url).join('\n');
      const fullContent = mediaUrls ? (content.trim() ? content + '\n\n' + mediaUrls : mediaUrls) : content;

      const kind = showPollCreator && pollOptions.filter(o => o.trim()).length >= 2 ? 1068 : 1;
      if (kind === 1068) {
        const validOptions = pollOptions.filter(o => o.trim());
        validOptions.forEach((opt, i) => tags.push(['option', String(i), opt]));
        tags.push(['polltype', 'single']);
        if (pollDuration > 0) tags.push(['closed_at', String(scheduledTimestamp + pollDuration * 3600)]);
      }

      const eventContent = kind === 1068 ? content : fullContent;

      // ── Sign the event NOW so the server can publish it later ──
      // The server-side scheduler (Netlify cron) needs a fully signed
      // event because it doesn't have access to the user's private key.
      // We use scheduledTimestamp as created_at so the event appears to
      // have been posted at the intended time.
      const pubkey = await signer.getPublicKey();
      const unsigned = {
        kind,
        content: eventContent,
        tags,
        pubkey,
        created_at: scheduledTimestamp,
      };
      const id = getEventHash(unsigned as any);
      const signedEvent = await signer.signEvent({ ...unsigned, id });

      // ── POST to server-side scheduler ──
      const relays = getRelays();
      const res = await fetch('/api/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signedEvent,
          scheduledAt: scheduledTimestamp,
          relays,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Server error' }));
        throw new Error(data.error || `Server returned ${res.status}`);
      }

      // Also save to client-side as a record (status tracking + queue UI)
      schedulePost({
        content: eventContent,
        kind,
        tags,
        scheduledAt: scheduledTimestamp,
        attachments: attachments.map(a => ({ url: a.url, type: a.type })),
      });

      // Reset form + clear draft
      clearDraft(replyTo?.id);
      setContent('');
      setAttachments([]);
      setContentLabels([]);
      setAttachmentSources([]);
      setLabelSuggestion(null);
      setShowPollCreator(false);
      setPollOptions(['', '']);
      setMentionedPubkeys([]);
      setShowScheduler(false);
      setScheduleDate('');
      setScheduleTime('');
      setScheduleConfirmed(true);
      setTimeout(() => setScheduleConfirmed(false), 3000);
      onPublished?.();

    } catch (err) {
      console.error('Schedule failed:', err);
      const msg = err instanceof Error ? err.message : 'Scheduling failed';

      // ── Fallback: client-side scheduling ──
      // If the server is unreachable (offline, deploy issue, etc.),
      // fall back to localStorage-based scheduling. The browser-side
      // scheduler will publish it if the tab is open at the scheduled time.
      try {
        const tags: string[][] = [];
        if (!replyTo) { tags.push(['t', 'bchnostr']); }
        if (replyTo) {
          const rootId = replyTo.rootId || replyTo.id;
          if (rootId !== replyTo.id) tags.push(['e', rootId, '', 'root']);
          tags.push(['e', replyTo.id, '', 'reply']);
          tags.push(['p', replyTo.pubkey]);
        }
        const mediaUrls = attachments.map(att => att.url).join('\n');
        const fullContent = mediaUrls ? (content.trim() ? content + '\n\n' + mediaUrls : mediaUrls) : content;
        const kind = showPollCreator && pollOptions.filter(o => o.trim()).length >= 2 ? 1068 : 1;

        schedulePost({
          content: kind === 1068 ? content : fullContent,
          kind,
          tags,
          scheduledAt: scheduledTimestamp,
          attachments: attachments.map(a => ({ url: a.url, type: a.type })),
        });

        // Still reset the form — the post is queued locally
        clearDraft(replyTo?.id);
        setContent('');
        setAttachments([]);
        setContentLabels([]);
        setAttachmentSources([]);
        setLabelSuggestion(null);
        setShowPollCreator(false);
        setPollOptions(['', '']);
        setMentionedPubkeys([]);
        setShowScheduler(false);
        setScheduleDate('');
        setScheduleTime('');
        setScheduleConfirmed(true);
        setScheduleError(`Server unavailable — saved locally. Keep this tab open until ${new Date(scheduledTimestamp * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.`);
        setTimeout(() => { setScheduleConfirmed(false); setScheduleError(null); }, 8000);
        onPublished?.();
      } catch {
        setScheduleError(msg);
        setTimeout(() => setScheduleError(null), 5000);
      }
    } finally {
      setScheduling(false);
    }
  };

  const handlePublish = async () => {
    if (!user || (!content.trim() && attachments.length === 0 && !showPollCreator)) return;
    // Enforce rate limit (replies are exempt — only original posts count)
    if (!replyTo && isRateLimited) return;

    setPublishing(true);
    setPublishError(null);

    try {
      const tags: string[][] = [];
      if (replyTo) {
        // NIP-10: include root tag if this is a nested reply (reply to a reply)
        const rootId = replyTo.rootId || replyTo.id;
        if (rootId !== replyTo.id) {
          tags.push(['e', rootId, '', 'root']);
        }
        tags.push(['e', replyTo.id, '', 'reply']);
        tags.push(['p', replyTo.pubkey]);
      }

      // Add a lightweight community tag on original posts, not replies.
      // BCH Nostr is about more than Bitcoin Cash, so we no longer stamp
      // #bch/#bitcoincash on every post — the client:BCHNostr tag already
      // surfaces posts in the community feed. Authors who want the BCH topic
      // tags can simply type #bch / #bitcoincash and they'll be preserved.
      // (Replies are left untagged so they don't flood the feed.)
      if (!replyTo) {
        tags.push(['t', 'bchnostr']);
      }

      // Add p-tags for mentioned users
      mentionedPubkeys.forEach(m => {
        if (!tags.some(t => t[0] === 'p' && t[1] === m.pubkey)) {
          tags.push(['p', m.pubkey]);
        }
      });

      // Also detect nostr:npub mentions in content and add p-tags
      const npubMatches = content.matchAll(/nostr:(npub1[a-z0-9]+)/g);
      for (const match of npubMatches) {
        try {
          const decoded = nip19.decode(match[1]);
          if (decoded.type === 'npub') {
            const pk = decoded.data as string;
            if (!tags.some(t => t[0] === 'p' && t[1] === pk)) {
              tags.push(['p', pk]);
            }
          }
        } catch {}
      }

      // Auto-resolve raw @username mentions into nostr:npub references.
      // This catches cases where users type @name without using the autocomplete.
      // We scan for @word patterns that are NOT already inside a nostr: reference,
      // look them up in the profile cache, and convert matches.
      // Scan for raw @username patterns and resolve them to nostr:npub references.
      // Uses a simple regex without lookbehind for broad browser compat.
      let postContent = content;
      const atMentionRegex = /(?:^|[\s(])@([a-zA-Z0-9_]{1,64})(?=[\s,.)!?:;]|$)/g;
      const atMatches = [...content.matchAll(atMentionRegex)];
      // Process in reverse order so indices stay valid after replacements
      for (let i = atMatches.length - 1; i >= 0; i--) {
        const m = atMatches[i];
        const username = m[1];
        const fullMatch = m[0];
        const matchIndex = m.index!;
        // Find where the @username starts within the full match (may have leading space)
        const atOffset = fullMatch.indexOf('@');
        const atAbsIndex = matchIndex + atOffset;
        // Skip if this @mention is inside a nostr: reference already
        const before = postContent.slice(Math.max(0, atAbsIndex - 10), atAbsIndex);
        if (before.includes('nostr:')) continue;
        // Try to find the user in the profile cache
        const found = findProfileByName(username);
        if (found) {
          const npub = nip19.npubEncode(found.pubkey);
          const atLen = 1 + username.length; // "@username"
          postContent = postContent.slice(0, atAbsIndex) + `nostr:${npub}` + postContent.slice(atAbsIndex + atLen);
          // Also add p-tag
          if (!tags.some(t => t[0] === 'p' && t[1] === found.pubkey)) {
            tags.push(['p', found.pubkey]);
          }
        }
      }

      // Extract any hashtags the author typed (use resolved content).
      // We now respect #bch / #bitcoincash too — they're only added when the
      // author actually writes them, deduped against tags already present.
      const hashtagMatches = postContent.matchAll(/#(\w{2,})/g);
      for (const match of hashtagMatches) {
        const tag = match[1].toLowerCase();
        if (!tags.some(t => t[0] === 't' && t[1] === tag)) {
          tags.push(['t', tag]);
        }
      }

      // Add media tags (imeta is the proper Nostr way — don't duplicate in content)
      attachments.forEach(att => {
        if (att.type === 'video') {
          tags.push(['imeta', `url ${att.url}`, 'm video/mp4']);
        } else if (att.type === 'audio') {
          const mime = att.url.endsWith('.mp3') ? 'audio/mpeg'
            : att.url.endsWith('.ogg') ? 'audio/ogg'
            : att.url.endsWith('.wav') ? 'audio/wav'
            : 'audio/webm';
          tags.push(['imeta', `url ${att.url}`, `m ${mime}`]);
        } else {
          tags.push(['imeta', `url ${att.url}`]);
        }
      });

      // ── NIP-32 content labels (self-reporting) ──────────────────
      if (contentLabels.length > 0) {
        tags.push(['L', 'content-origin']);
        for (const label of contentLabels) {
          tags.push(['l', label, 'content-origin']);
        }
      }

      // Append media URLs to content so other clients can render them
      const mediaUrls = attachments.map(att => att.url).join('\n');
      const fullContent = mediaUrls ? (postContent.trim() ? postContent + '\n\n' + mediaUrls : mediaUrls) : postContent;

      if (showPollCreator && pollOptions.filter(o => o.trim()).length >= 2) {
        // Publish as poll (kind 1068)
        const validOptions = pollOptions.filter(o => o.trim());
        validOptions.forEach((opt, i) => tags.push(['option', String(i), opt]));
        tags.push(['polltype', 'single']);
        // Polls live on the Polls page, which queries the #bchnostr tag.
        if (!tags.some(t => t[0] === 't' && t[1] === 'bchnostr')) tags.push(['t', 'bchnostr']);
        if (pollDuration > 0) {
          tags.push(['closed_at', String(Math.floor(Date.now() / 1000) + pollDuration * 3600)]);
        }

        await publish({
          kind: 1068,
          content: postContent,
          tags,
          created_at: Math.floor(Date.now() / 1000),
        });
      } else {
        await publish({
          kind: 1,
          content: fullContent,
          tags,
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      clearDraft(replyTo?.id);
      setContent('');
      setAttachments([]);
      setContentLabels([]);
      setAttachmentSources([]);
      setLabelSuggestion(null);
      setShowPollCreator(false);
      setPollOptions(['', '']);
      setMentionedPubkeys([]);

      // Bump local counter so rate limit kicks in immediately
      if (!replyTo) {
        setLocalPostsBump(prev => prev + 1);
      }

      // ── Referral confirmation ────────────────────────────────
      // On the user's first BCH post, publish a referral confirmation
      // event so the referrer gets credited on the leaderboard.
      // Only mark as confirmed AFTER the publish succeeds.
      if (!hasConfirmedReferral()) {
        const referrer = getStoredReferrer();
        if (referrer && user) {
          publish({
            kind: REFERRAL_KIND,
            content: '',
            tags: [
              ['d', REFERRAL_DRTAG],
              ['ref', referrer],
              ['p', referrer],   // indexed p-tag so relays can filter by referrer pubkey
              ['action', 'first-post'],
            ],
            created_at: Math.floor(Date.now() / 1000),
          }).then(() => {
            markReferralConfirmed();
            clearStoredReferrer();
          }).catch(() => {
            // Don't mark as confirmed — will retry on next post or page load
          });
        }
      }

      onPublished?.();
    } catch (e) {
      console.error('Publish failed:', e);
      // Surface the failure so the user knows the post didn't reach any relay
      // and their content is preserved for a retry.
      setPublishError(
        e instanceof Error && e.message
          ? e.message
          : 'Your post could not be published. Please try again.'
      );
    } finally {
      setPublishing(false);
    }
  };

  if (!user) {
    return (
      <div className="border-b border-border p-4 text-center">
        <p className="text-sm text-muted-foreground">Log in to post and interact with the community.</p>
      </div>
    );
  }

  return (
    <div className="border-b border-border p-4">
      <div className="flex gap-3">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <BCHLogo size={20} />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleInput}
              onPaste={handlePaste}
              onFocus={() => { /* draft indicator stays visible until user edits or discards */ }}
              placeholder={placeholder || (replyTo ? 'Write your reply...' : "What's happening in BCH?")}
              className="w-full bg-transparent resize-none text-sm placeholder:text-muted-foreground focus:outline-none min-h-[100px] max-h-[400px]"
              rows={4}
              onKeyDown={e => {
                if (mentionDropdown && mentionSuggestions.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setMentionIndex(i => Math.min(i + 1, mentionSuggestions.length - 1));
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setMentionIndex(i => Math.max(i - 1, 0));
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    const s = mentionSuggestions[mentionIndex];
                    if (s) insertMention(s.pubkey, s.name);
                    return;
                  }
                }
                if (e.key === 'Escape' && mentionDropdown) {
                  e.preventDefault();
                  setMentionDropdown(false);
                  setMentionQuery('');
                } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  // If scheduler is open with a time set, schedule instead of posting
                  if (showScheduler && scheduledTimestamp) {
                    handleSchedule();
                  } else {
                    handlePublish();
                  }
                }
              }}
            />

            {/* Mention dropdown — positioned above textarea to avoid being cut off in modals */}
            {mentionDropdown && mentionSuggestions.length > 0 && (
              <div className="absolute left-0 bottom-full mb-1 w-72 bg-popover border border-border rounded-lg shadow-xl z-30 max-h-56 overflow-y-auto">
                <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-b border-border flex items-center gap-1">
                  <AtSign className="w-3 h-3" /> Mention a user — ↑↓ to navigate, Enter to select
                  {liveSearching && <Loader2 className="w-2.5 h-2.5 animate-spin ml-auto" />}
                </div>
                {mentionSuggestions.map((s, i) => (
                  <button
                    key={s.pubkey}
                    onClick={() => insertMention(s.pubkey, s.name)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                      i === mentionIndex ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                    }`}
                  >
                    {s.picture ? (
                      <img src={s.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <User className="w-3.5 h-3.5 text-primary/60" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate">{s.name}</p>
                      {s.nip05 && <p className="text-[10px] text-muted-foreground truncate">{s.nip05}</p>}
                    </div>
                    {s.isFollow && <span className="text-[9px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full shrink-0">following</span>}
                  </button>
                ))}
              </div>
            )}
            {mentionDropdown && mentionSuggestions.length === 0 && (
              <div className="absolute left-0 bottom-full mb-1 w-64 bg-popover border border-border rounded-lg shadow-xl z-30 p-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  {liveSearching ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Searching Nostr...</>
                  ) : (
                    <><AtSign className="w-3 h-3" />
                    {mentionQuery.length < 2 ? 'Keep typing to search...' : 'No users found. Try a different name.'}</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Attachments preview */}
          {attachments.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {attachments.map((att, i) => (
                <div key={i} className="relative group">
                  {att.type === 'audio' ? (
                    <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2 border border-border pr-7">
                      <Music className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                        {att.name || 'Voice message'}
                      </span>
                    </div>
                  ) : att.type === 'video' ? (
                    <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center border border-border">
                      <Film className="w-6 h-6 text-primary" />
                    </div>
                  ) : (
                    <img src={att.url} alt="" className="w-20 h-20 rounded-lg object-cover" />
                  )}
                  <button
                    onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                    className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  {att.type === 'video' && (
                    <span className="absolute bottom-0.5 left-0.5 text-[8px] bg-black/60 text-white px-1 rounded">VIDEO</span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Recording indicator */}
          {recording && (
            <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-red-500">Recording {fmtRecordTime(recordSeconds)}</span>
              <button
                onClick={stopRecording}
                className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors"
              >
                <MicOff className="w-3 h-3" /> Stop
              </button>
            </div>
          )}

          {/* Compression Progress */}
          {compressing && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Compressing video... {compressionProgress}%</span>
                <span className="text-[10px] text-muted-foreground ml-auto">Shrinking for faster upload</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-amber-500 rounded-full transition-all duration-300" style={{ width: `${compressionProgress}%` }} />
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && !compressing && (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Uploading media... {uploadProgress}%</span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mt-2 flex items-start gap-2 text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <span>{uploadError.message}</span>
                {uploadError.upgradeUrl && (
                  <a
                    href={uploadError.upgradeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mt-1 text-primary hover:underline font-medium"
                  >
                    Upgrade at nostr.build for up to 450MB uploads &rarr;
                  </a>
                )}
              </div>
              <button onClick={() => setUploadError(null)} className="shrink-0 mt-0.5">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Publish Error — post didn't reach any relay */}
          {publishError && (
            <div className="mt-2 flex items-start gap-2 text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0">{publishError}</span>
              <button onClick={() => setPublishError(null)} className="shrink-0 mt-0.5">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Poll Creator */}
          {showPollCreator && (
            <div className="mt-3 space-y-2 p-3 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <BarChart3 className="w-4 h-4 text-primary" /> Create Poll
                </span>
                <button onClick={() => setShowPollCreator(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={opt}
                    onChange={e => {
                      const newOpts = [...pollOptions];
                      newOpts[i] = e.target.value;
                      setPollOptions(newOpts);
                    }}
                    placeholder={`Option ${i + 1}`}
                    className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  {pollOptions.length > 2 && (
                    <button onClick={() => setPollOptions(prev => prev.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button
                  onClick={() => setPollOptions(prev => [...prev, ''])}
                  className="text-xs text-primary hover:underline"
                >
                  + Add option
                </button>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Duration:</span>
                <select
                  value={pollDuration}
                  onChange={e => setPollDuration(Number(e.target.value))}
                  className="text-xs bg-muted rounded px-2 py-1 focus:outline-none"
                >
                  <option value={1}>1 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={24}>1 day</option>
                  <option value={72}>3 days</option>
                  <option value={168}>1 week</option>
                </select>
              </div>
            </div>
          )}

          {/* GIF Picker */}
          {showGifPicker && (
            <GifPicker
              onSelect={(url) => {
                setAttachments(prev => [...prev, { url, type: 'image' }]);
                setShowGifPicker(false);
                handleSourceDetected('gif-picker');
              }}
              onClose={() => setShowGifPicker(false)}
            />
          )}

          {/* Hyperlink input */}
          {showLinkInput && (
            <div className="mt-3 p-3 border border-border rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <Link2 className="w-4 h-4 text-primary" /> Insert Link
                </span>
                <button onClick={() => setShowLinkInput(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <input
                ref={linkUrlRef}
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); insertLink(); }
                  if (e.key === 'Escape') { e.preventDefault(); setShowLinkInput(false); }
                }}
                placeholder="https://example.com"
                className="w-full bg-muted rounded-lg px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <input
                value={linkText}
                onChange={e => setLinkText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); insertLink(); }
                  if (e.key === 'Escape') { e.preventDefault(); setShowLinkInput(false); }
                }}
                placeholder="Display text (optional)"
                className="w-full bg-muted rounded-lg px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground">URLs are auto-linked when the post is displayed.</p>
                <button
                  onClick={insertLink}
                  disabled={!linkUrl.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Link2 className="w-3 h-3" /> Add Link
                </button>
              </div>
            </div>
          )}

          {/* ── Auto-label suggestion banner ─────────────────────────── */}
          {labelSuggestion && !labelSuggestion.dismissed && (
            <div className={`mt-2 flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${
              labelSuggestion.type === 'original-detected'
                ? 'bg-emerald-500/10 border-emerald-500/20'
                : 'bg-amber-500/10 border-amber-500/20'
            }`}>
              {labelSuggestion.type === 'original-detected' ? (
                <>
                  <CameraIcon className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                    Camera photo detected — tagged as Original Content
                  </span>
                  <span className="text-emerald-600/60 dark:text-emerald-400/60">📸</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <span className="text-amber-700 dark:text-amber-400">
                    Pasted image — is this AI-generated or edited?
                  </span>
                  <button
                    onClick={() => {
                      setContentLabels(prev => prev.includes('ai-generated') ? prev : [...prev, 'ai-generated']);
                      setLabelSuggestion(s => s ? { ...s, dismissed: true } : null);
                    }}
                    className="ml-auto text-[10px] font-medium text-amber-600 dark:text-amber-400 hover:underline whitespace-nowrap"
                  >
                    Tag AI
                  </button>
                </>
              )}
              <button
                onClick={() => setLabelSuggestion(s => s ? { ...s, dismissed: true } : null)}
                className="shrink-0 text-muted-foreground hover:text-foreground ml-auto"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* ── Content labels display + picker ──────────────────────── */}
          {contentLabels.length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {contentLabels.map(label => (
                <span
                  key={label}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    label === 'original'
                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                      : label === 'ai-generated'
                      ? 'bg-purple-500/10 border-purple-500/20 text-purple-600 dark:text-purple-400'
                      : 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400'
                  }`}
                >
                  {label === 'original' && <><CameraIcon className="w-2.5 h-2.5" /> Original Content</>}
                  {label === 'ai-generated' && <><Sparkles className="w-2.5 h-2.5" /> AI Generated</>}
                  {label === 'manipulated' && <><PenTool className="w-2.5 h-2.5" /> Edited/Manipulated</>}
                  <button
                    onClick={() => setContentLabels(prev => prev.filter(l => l !== label))}
                    className="hover:opacity-70 ml-0.5"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Rate limit notice for new accounts */}
          {showRateLimitInfo && !replyTo && (
            <div className="mt-2 flex items-start gap-2 text-xs rounded-lg px-3 py-2 bg-amber-500/10 border border-amber-500/20">
              <Shield className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div>
                {isRateLimited ? (
                  <p className="text-amber-700 dark:text-amber-400 font-medium">
                    Posting limit reached for today. New accounts unlock more posts as they become established members.
                  </p>
                ) : (
                  <p className="text-muted-foreground">
                    {maturity.isNewbie
                      ? `New account: ${effectiveRemaining} post${effectiveRemaining === 1 ? '' : 's'} remaining today.`
                      : `${effectiveRemaining} post${effectiveRemaining === 1 ? '' : 's'} remaining today. Full access unlocks in ${7 - maturity.ageDays} day${7 - maturity.ageDays === 1 ? '' : 's'}.`
                    }
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Schedule Picker */}
          {showScheduler && (
            <div className="mt-3 p-3 border border-border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium flex items-center gap-1.5">
                  <CalendarClock className="w-4 h-4 text-primary" /> Schedule Post
                </span>
                <button onClick={() => setShowScheduler(false)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Quick presets */}
              <div className="flex flex-wrap gap-1.5">
                {getSchedulePresets().map(preset => {
                  const ts = preset.getTimestamp();
                  const isActive = scheduledTimestamp === ts;
                  return (
                    <button
                      key={preset.label}
                      onClick={() => {
                        const dt = new Date(ts * 1000);
                        setScheduleDate(dt.toISOString().split('T')[0]);
                        setScheduleTime(dt.toTimeString().slice(0, 5));
                      }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>

              {/* Custom date/time */}
              <div className="flex gap-2">
                <input
                  type="date"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={e => setScheduleTime(e.target.value)}
                  className="flex-1 bg-muted rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              {scheduledTimestamp && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                  <Clock className="w-3 h-3" /> Will post: {formatScheduledTime(scheduledTimestamp)}
                </p>
              )}
              <button
                onClick={handleSchedule}
                disabled={scheduling || !scheduledTimestamp || (!content.trim() && attachments.length === 0)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {scheduling ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Signing &amp; scheduling...</>
                ) : (
                  <><CalendarClock className="w-4 h-4" /> Schedule Post</>
                )}
              </button>
            </div>
          )}

          {/* Schedule confirmed toast */}
          {scheduleConfirmed && (
            <div className="mt-2 flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 rounded-lg px-3 py-2 border border-emerald-500/20">
              <CalendarClock className="w-3.5 h-3.5" />
              Post scheduled! It will publish automatically.
            </div>
          )}

          {/* Schedule error */}
          {scheduleError && (
            <div className="mt-2 flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2 border border-amber-500/20">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{scheduleError}</span>
              <button onClick={() => setScheduleError(null)} className="shrink-0 ml-auto">
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Draft restored indicator */}
          {draftRestored && (content.trim() || attachments.length > 0) && (
            <div className="mt-2 flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium">Draft restored</span>
              {attachments.length > 0 && (
                <span className="text-[10px] opacity-70">· {attachments.length} attachment{attachments.length !== 1 ? 's' : ''}</span>
              )}
              <button
                onClick={() => {
                  clearDraft(replyTo?.id);
                  setContent('');
                  setAttachments([]);
                  setContentLabels([]);
                  setAttachmentSources([]);
                  setLabelSuggestion(null);
                  setShowPollCreator(false);
                  setPollOptions(['', '']);
                  setDraftRestored(false);
                }}
                className="ml-auto text-[10px] font-medium text-red-400 hover:text-red-500 transition-colors"
              >
                Discard
              </button>
            </div>
          )}

          {/* Action bar — wraps on small screens so the Post button is always reachable */}
          <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-0.5 sm:gap-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,image/heic,image/heif,video/mp4,video/webm,video/quicktime,video/x-msvideo,video/*,audio/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = ''; // Reset so same file can be picked again
                  // Start source detection in parallel with upload
                  const sourcePromise = file.type.startsWith('image/') || file.type.startsWith('video/')
                    ? detectMediaSource(file) : Promise.resolve('file-picker' as AttachmentSource);
                  setUploadError(null);
                  setCompressing(false);
                  setCompressionProgress(0);
                  setUploading(true);
                  setUploadProgress(0);
                  try {
                    const result = await uploadMedia(file, setUploadProgress, (cp) => {
                      if (cp.stage === 'loading' || cp.stage === 'compressing') {
                        setCompressing(true);
                        setCompressionProgress(cp.percent);
                      } else {
                        setCompressing(false);
                        setCompressionProgress(100);
                      }
                    });
                    setAttachments(prev => [...prev, result]);
                    // Process auto-detection result
                    const source = await sourcePromise;
                    handleSourceDetected(source);
                  } catch (err) {
                    console.error('Media upload failed:', err);
                    setUploadError(err instanceof UploadSizeError
                      ? { message: err.message, upgradeUrl: err.upgradeUrl }
                      : { message: err instanceof Error ? err.message : 'Upload failed' });
                  } finally {
                    setUploading(false);
                    setUploadProgress(0);
                    setCompressing(false);
                    setCompressionProgress(0);
                  }
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || compressing}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${(uploading || compressing) ? 'text-primary' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                title="Add image or video"
              >
                {(uploading || compressing) ? <Loader2 className="w-[18px] h-[18px] sm:w-5 sm:h-5 animate-spin" /> : <Camera className="w-[18px] h-[18px] sm:w-5 sm:h-5" />}
              </button>
              <button
                onClick={() => setShowGifPicker(!showGifPicker)}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${showGifPicker ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                title="Add GIF"
              >
                <Film className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setShowPollCreator(!showPollCreator)}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${showPollCreator ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                title="Create poll"
              >
                <BarChart3 className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
              </button>

              {/* Content label toggle */}
              <div className="relative">
                <button
                  onClick={() => setShowLabelPicker(!showLabelPicker)}
                  className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                    showLabelPicker || contentLabels.length > 0
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                  }`}
                  title="Content labels (AI, Original, Edited)"
                >
                  <Tag className="w-[18px] h-[18px] sm:w-5 sm:h-5" />
                  {contentLabels.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-primary text-primary-foreground text-[8px] rounded-full flex items-center justify-center font-bold">
                      {contentLabels.length}
                    </span>
                  )}
                </button>
                {showLabelPicker && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowLabelPicker(false)} />
                    <div className="absolute left-0 bottom-10 z-30 w-56 bg-popover border border-border rounded-xl shadow-lg py-1.5">
                      <p className="px-3 py-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Content Labels</p>
                      {([
                        { id: 'original' as ContentLabel, label: 'Original Content', icon: <CameraIcon className="w-4 h-4" />, desc: 'Your own photo/video', color: 'text-emerald-500' },
                        { id: 'ai-generated' as ContentLabel, label: 'AI Generated', icon: <Sparkles className="w-4 h-4" />, desc: 'Made with AI tools', color: 'text-purple-500' },
                        { id: 'manipulated' as ContentLabel, label: 'Edited / Manipulated', icon: <PenTool className="w-4 h-4" />, desc: 'Photo/video was altered', color: 'text-amber-500' },
                      ]).map(item => {
                        const isActive = contentLabels.includes(item.id);
                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              setContentLabels(prev =>
                                prev.includes(item.id)
                                  ? prev.filter(l => l !== item.id)
                                  : [...prev, item.id]
                              );
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2.5 transition-colors ${
                              isActive ? 'bg-primary/5' : ''
                            }`}
                          >
                            <span className={item.color}>{item.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium ${isActive ? 'text-foreground' : ''}`}>{item.label}</p>
                              <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                            </div>
                            {isActive && (
                              <span className="w-4 h-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                                <Check className="w-2.5 h-2.5 text-primary-foreground" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                      <div className="mx-3 mt-1.5 pt-1.5 border-t border-border">
                        <p className="text-[9px] text-muted-foreground leading-relaxed">
                          Labels are embedded in your post using NIP-32 and visible to all Nostr clients.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Audio file upload */}
              <input
                ref={audioFileInputRef}
                type="file"
                accept="audio/*,.mp3,.ogg,.wav,.aac,.flac,.m4a,.opus"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  e.target.value = '';
                  setUploadError(null);
                  setUploading(true);
                  setUploadProgress(0);
                  try {
                    const url = await uploadAudio(file, setUploadProgress);
                    setAttachments(prev => [...prev, { url, type: 'audio', name: file.name }]);
                  } catch (err) {
                    setUploadError(err instanceof UploadSizeError
                      ? { message: err.message, upgradeUrl: err.upgradeUrl }
                      : { message: err instanceof Error ? err.message : 'Audio upload failed' });
                  } finally {
                    setUploading(false);
                    setUploadProgress(0);
                  }
                }}
              />

              {/* ── Desktop-only buttons (inline) ── */}
              {/* Mic record button */}
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={uploading}
                className={`p-1.5 sm:p-2 rounded-lg transition-colors ${recording ? '' : 'hidden sm:flex'} ${
                  recording
                    ? 'text-red-500 bg-red-500/10 animate-pulse'
                    : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/5'
                }`}
                title={recording ? 'Stop recording' : 'Record voice message'}
              >
                {recording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              {/* Emoji picker trigger — desktop only */}
              <div className="relative hidden sm:block">
                <button
                  ref={emojiButtonRef}
                  onClick={() => setShowEmojiPicker(p => !p)}
                  className={`p-2 rounded-lg transition-colors ${showEmojiPicker ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/5'}`}
                  title="Emoji"
                >
                  <Smile className="w-5 h-5" />
                </button>
                {showEmojiPicker && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowEmojiPicker(false)} />
                    <EmojiPicker
                      onSelect={insertEmoji}
                      onClose={() => setShowEmojiPicker(false)}
                    />
                  </>
                )}
              </div>

              {/* Hyperlink button — desktop only */}
              <button
                onClick={() => setShowLinkInput(!showLinkInput)}
                className={`hidden sm:flex p-2 rounded-lg transition-colors ${showLinkInput ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                title="Insert link"
              >
                <Link2 className="w-5 h-5" />
              </button>

              {/* ── Mobile "more tools" menu ── */}
              <div className="relative sm:hidden">
                <button
                  onClick={() => setShowMoreTools(!showMoreTools)}
                  className={`p-1.5 rounded-lg transition-colors ${showMoreTools ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                  title="More tools"
                >
                  <MoreHorizontal className="w-[18px] h-[18px]" />
                </button>
                {showMoreTools && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowMoreTools(false)} />
                    <div className="absolute left-0 bottom-10 z-30 w-48 bg-popover border border-border rounded-xl shadow-lg py-1">
                      <button
                        onClick={() => { recording ? stopRecording() : startRecording(); setShowMoreTools(false); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted flex items-center gap-2.5"
                      >
                        {recording ? <MicOff className="w-4 h-4 text-red-500" /> : <Mic className="w-4 h-4" />}
                        {recording ? 'Stop recording' : 'Voice message'}
                      </button>
                      <button
                        onClick={() => { setShowLinkInput(!showLinkInput); setShowMoreTools(false); }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted flex items-center gap-2.5"
                      >
                        <Link2 className="w-4 h-4" /> Insert link
                      </button>
                      {!replyTo && content.trim() && (
                        <button
                          onClick={() => { setShowCrossPost(true); setShowMoreTools(false); }}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted flex items-center gap-2.5 text-purple-500"
                        >
                          <Share2 className="w-4 h-4" /> Cross-post
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 ml-auto">
              {/* Schedule button — for original posts */}
              {!replyTo && (
                <button
                  onClick={() => setShowScheduler(!showScheduler)}
                  className={`p-1.5 sm:p-2 rounded-full border transition-colors ${
                    showScheduler
                      ? 'border-primary text-primary bg-primary/10'
                      : 'border-border text-muted-foreground hover:text-primary hover:border-primary/40'
                  }`}
                  title="Schedule post"
                >
                  <Clock className="w-3.5 h-3.5" />
                </button>
              )}
              {/* Cross-post button — only show when there's content and no reply, hidden on small screens */}
              {!replyTo && content.trim() && (
                <button
                  onClick={() => setShowCrossPost(true)}
                  className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary/40 text-sm font-medium transition-colors"
                  title="Share to other platforms"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  <span className="text-xs">Cross-post</span>
                </button>
              )}
              {/* When scheduler is open with a time selected, the main button
                  becomes "Schedule" instead of "Post" to prevent accidental
                  immediate publishing. The user's intent is clearly to schedule. */}
              {showScheduler && scheduledTimestamp ? (
                <button
                  onClick={handleSchedule}
                  disabled={scheduling || uploading || compressing || (!content.trim() && attachments.length === 0)}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  {scheduling ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <CalendarClock className="w-4 h-4" />
                  )}
                  Schedule
                </button>
              ) : (
                <button
                  onClick={handlePublish}
                  disabled={publishing || uploading || compressing || (!content.trim() && attachments.length === 0) || (!replyTo && isRateLimited)}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                  title={!replyTo && isRateLimited ? 'Posting limit reached for today' : undefined}
                >
                  {publishing ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {replyTo ? 'Reply' : 'Post'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Cross-post modal */}
      {showCrossPost && (
        <CrossPostModal
          content={content}
          onClose={() => setShowCrossPost(false)}
        />
      )}
    </div>
  );
}

// GifPicker is now imported from ./GifPicker

